import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { ButtonUI } from "../../../../../components/Button";
import {
  type Organization,
  useUpdateOrganizationRestApi,
} from "../../../../../hooks/useOrganizationsRestApi";
import { EditOrganizationModal } from "./EditOrganizationModal";

interface OrganizationsTableProps {
  organizations: Organization[];
  isLoading: boolean;
  onRefresh: () => void;
}

export const OrganizationsTable: FC<OrganizationsTableProps> = ({
  organizations,
  isLoading,
  onRefresh,
}) => {
  const [selectedOrgs, setSelectedOrgs] = useState<Set<string>>(new Set());
  const { updateOrganization } = useUpdateOrganizationRestApi();
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrgs(new Set(organizations.map(org => org.id)));
    } else {
      setSelectedOrgs(new Set());
    }
  };

  const handleSelectOrg = (orgId: string, checked: boolean) => {
    const newSelected = new Set(selectedOrgs);
    if (checked) {
      newSelected.add(orgId);
    } else {
      newSelected.delete(orgId);
    }
    setSelectedOrgs(newSelected);
  };

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleToggleStatus = async (org: Organization) => {
    try {
      await updateOrganization(org.id, {
        name: org.name,
        description: org.description || undefined,
        isActive: !org.isActive,
      });
      onRefresh();
    } catch (_error) {
      alert("ステータスの更新に失敗しました");
    }
  };

  const handleEditOrganization = (org: Organization) => {
    setEditingOrg(org);
  };

  const handleEditSuccess = () => {
    setEditingOrg(null);
    onRefresh();
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">読み込み中...</p>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-gray-400 text-6xl mb-4">📋</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          組織が見つかりません
        </h3>
        <p className="text-gray-600 mb-4">
          検索条件を変更するか、新しい組織を作成してください。
        </p>
        <ButtonUI
          onClick={onRefresh}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          再読み込み
        </ButtonUI>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              {organizations.length}件の組織が見つかりました
            </span>
            {selectedOrgs.size > 0 && (
              <span className="text-sm text-blue-600">
                {selectedOrgs.size}件選択中
              </span>
            )}
          </div>
          <ButtonUI
            onClick={onRefresh}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
          >
            🔄 更新
          </ButtonUI>
        </div>
      </div>

      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <input
                type="checkbox"
                checked={
                  selectedOrgs.size === organizations.length &&
                  organizations.length > 0
                }
                onChange={e => handleSelectAll(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              組織名
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              説明
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              従業員数
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ステータス
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              更新日時
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              操作
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {organizations.map(org => (
            <tr
              key={org.id}
              className={`hover:bg-gray-50 ${
                selectedOrgs.has(org.id) ? "bg-blue-50" : ""
              }`}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={selectedOrgs.has(org.id)}
                  onChange={e => handleSelectOrg(org.id, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {org.name}
                    </div>
                    <div className="text-sm text-gray-500">ID: {org.id}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 max-w-xs truncate">
                  {org.description ?? "説明なし"}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {org.employeeCount ?? 0}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    org.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {org.isActive ? "アクティブ" : "非アクティブ"}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(org.updatedAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                <Link
                  to={`/admin/organizations/${org.id}`}
                  className="text-blue-600 hover:text-blue-900 font-medium"
                >
                  詳細
                </Link>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => handleEditOrganization(org)}
                  className="text-indigo-600 hover:text-indigo-900 font-medium"
                >
                  編集
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => handleToggleStatus(org)}
                  className={`font-medium ${
                    org.isActive
                      ? "text-red-600 hover:text-red-900"
                      : "text-green-600 hover:text-green-900"
                  }`}
                >
                  {org.isActive ? "停止" : "有効化"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 編集モーダル */}
      <EditOrganizationModal
        isOpen={editingOrg !== null}
        organization={editingOrg}
        onClose={() => setEditingOrg(null)}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
